import { Platform } from 'react-native';
import Storage from '../utils/Storage';
import { getDefaultGym } from '../utils/gyms';

import {
  environmentSetConfig, stagingConfig, devConfig, DEFAULT_ACCENT_COLOR,
} from './environments';
import build from './build';
import { Theme } from '../index';

export const GOOGLE_SIGN_IN_ANDROID_CLIENT_ID = process.env.GOOGLE_SIGN_IN_ANDROID_CLIENT_ID
    || '156358306145-uv1lnobbj9mteien45gjb9ivgeoq8neu.apps.googleusercontent.com';
export const GOOGLE_SIGN_IN_IOS_CLIENT_ID = process.env.GOOGLE_SIGN_IN_IOS_CLIENT_ID
    || '156358306145-fa11vv88klv04okdunv1e3i9gjr430ct.apps.googleusercontent.com';
export { build };

// this looks for the package in the apps at runtime
// , not the shared components so fails when running without apps
try {
  var { version } = require('fitbeat-version');
} catch (err) { // if not
  version = 'no_package.json_version';
}

export const getConfig = () => {
  if (ENVIRONMENT.name === 'dev') {
    return devConfig;
  }
  if (ENVIRONMENT.name === 'staging') {
    return stagingConfig;
  }

  // fallback to the environment set configs
  return environmentSetConfig;
};

export const getGymName = () => ENVIRONMENT.gymName;

export const isProdFeatureToggle = () => {
  getConfig().displayName.search(/Default/g) != -1;
};

export const getApiConfig = () => {
  const config = getConfig();

  return {
    API_BASE_URL: config.apiBaseUrl,
    INFlUENCER_BASE_URL: `${(config.apiBaseUrl)}/api/influencers`,
    PLATFORM,
    BUILD_NUMBER,
    VERSION_NUMBER,
  };
};

export const getUrls = () => {
  const config = getConfig();

  const newPrimary = Theme.PRIMARY.replace('#', '');
  Object.entries(config.pages).forEach(([page, uri]) => {
    config.pages[page] = uri.replace(DEFAULT_ACCENT_COLOR, newPrimary);
  });

  return {
    BAIT_SHARE_URL,
    ALL_TERMS_AND_CONDITIONS: config.pages.allTermsAndCondition,
    FAQ_PAGE: config.pages.faq,
    EXERCISE_GUIDE: config.pages.exerciseGuide,
    FAQ_PAGE_WITH_BASIC_AUTH: config.pages.faqWithBasicAuth,
    MEMBERSHIP_PLANS: config.pages.membershipPlans,
    GYM_WEBPAGE: config.pages.gymWebPage,
    CALENDAR_BOOKING: config.pages.calendarBooking,
    PAYMENT_ROUTER: `${config.apiBaseUrl}/api/payment-router`,
    NUTRITION_SCIENCE: config.pages.nutritionScience,
    SHOP_GEAR: config.pages.shopGearUrl,
    SCREEN_CASTING_FAQ_IOS: config.pages.screenCastingFaqIos,
    SCREEN_CASTING_FAQ_ANDROID: config.pages.screenCastingFaqAndroid,
    HEART_RATE_FAQ: config.pages.heartRateFaq,
    STUDIO_HRM_INSTRUCTIONS: config.pages.studioHRMInstructions,
    FAQ_LEARN_ABOUT_OUR_SCIENCE: config.pages.learnMoreAboutOurSciencFaq,
    FAQ_SCALE_SCIENCE: config.pages.scaleScienceFaq,
    FAQ_LEARN_HOW_TO_REACH_TARGET: config.pages.learnHowToReachTargetFaq,
    FAQ_LEARN_HOW_TO_REACH_TARGET_LEAN_AND_FIT: config.pages.learnHowToReachTargetLeanAndFitFaq,
    FAQ_LEARN_HOW_TO_REACH_TARGET_MUSCLE_AND_STRENGTH: config.pages.learnHowToReachTargetMuscleAndStrengthFaq,
    FAQ_CONNECT_TRACKING_DEVICE_ANDROID: config.pages.connectTrackingDeviceAndroidFaq,
    FAQ_CONNECT_TRACKING_DEVICE_IOS: config.pages.connectTrackingDeviceIosFaq,
    GOAL_OVERVIEW_NUTRITION_AND_WORKOUT: config.pages.goalOverviewNutritionAndWorkout,
    FAQ_LEARN_HOW_TO_REACH_TARGET_ALL_ROUNDER: config.pages.learnHowToReachTargetAllRounderFaq,
    APPLE_SIGNIN_RETURN_URL: config.pages.appleSignInReturnUrl,
    WEBSITE_STUDIO: config.pages.websiteStudio,
    EQUIPMENT_SETUP: config.pages.equipmentSetup,
  };
};

export const getDefaultGymName = () => getConfig().defaultGym;

export const getStripePublishableKey = () => getConfig().stripePublishableKey;

export const BUILD_NUMBER = process.env.BITRISE_BUILD_NUMBER || 'local-build';

export const VERSION_NUMBER = version || 'local-build';

export const PLATFORM = Platform.OS;

export const BUGSNAG_API_KEY = 'b59b86b80ef9138eb4b43fc296bddd80';
export const BAIT_SHARE_URL = process.env.BAIT_SHARE_URL || 'www.fitbeat.com';

export const PLAY_STORE_LINK = 'https://play.google.com/store/apps/details?id=com.fitillion.android';
export const APP_STORE_LINK = 'https://apps.apple.com/us/app/fitillion/id1596703533?mt=8';

// make timeout long - we only want to rely on timeout as last resort
export const WAITING_FOR_HR_FROM_CONNECTED_APP_TIMEOUT_IN_MS = 30000;

export const ENVIRONMENT = {
  // values set in reset below
};

export const gymSupportedCountries = [
  'Australia',
];

async function saveBaseUrlFromCurrentConfig() {
  const config = getConfig();
  await Storage.set('apiBaseUrl', config.apiBaseUrl);
}

export const environmentToggler = {
  defaultGym: getDefaultGym(),
  getAvailableEnvironments: () => [
    { id: 'ci', display: environmentSetConfig.displayName },
    { id: 'staging', display: stagingConfig.displayName },
    { id: 'dev', display: devConfig.displayName },
  ],
  reset: async (changeHelper) => {
    console.log('[ENV] environment reset to "ci"');
    Object.assign(ENVIRONMENT, {
      toggled: false,
      name: 'ci',
      gymName: environmentSetConfig.defaultGym,
    });
    await Storage.remove('toggledEnvironment');
    await Storage.remove('gymName');
    await saveBaseUrlFromCurrentConfig(); // always save, even on reset, to have it available in native code
    changeHelper('reset');
  },
  set: async (name, changeHelper) => {
    Object.assign(ENVIRONMENT, {
      toggled: true,
      name,
    });
    console.log(`[ENV] environment toggled to ${name}`);
    await Storage.set('toggledEnvironment', name);
    await saveBaseUrlFromCurrentConfig();
    const gymName = await Storage.get('gymName', this.defaultGym);
    Object.assign(ENVIRONMENT, { gymName });
    changeHelper('environment toggled');
  },
  load: async (changeHelper) => {
    const name = await Storage.get('toggledEnvironment', null);
    const gymName = await Storage.get('gymName', this.defaultGym);
    if (name) {
      console.log(`[ENV] loaded persisted value for toggledEnvironment of ${name}`);
      Object.assign(ENVIRONMENT, { gymName });
      await environmentToggler.set(name, changeHelper);
    } else {
      console.log('[ENV] loading default');
      await environmentToggler.reset(changeHelper);
    }

    const config = getConfig();
    console.log(`[ENV] loaded ${ENVIRONMENT.name}`, {
      configName: config.environmentName,
      variant: config.variant.name,
    });
  },
  installLoginTerminalConfigs: (configSets) => {
    environmentSetConfig.loginTerminal = configSets.environmentSetConfig || {};
    stagingConfig.loginTerminal = configSets.stagingConfig || {};
    devConfig.loginTerminal = configSets.devConfig || {};
    console.log('[ENV] built login terminal config values');
  },
  setGym: async (gymName, changeHelper) => {
    Object.assign(ENVIRONMENT, { gymName });
    await Storage.set('gymName', gymName);
    changeHelper();
  },
};

export const overrideConfig = (newConfig) => {
  Object.assign(stagingConfig, newConfig);
  Object.assign(devConfig, newConfig);
  Object.assign(environmentSetConfig, newConfig);
};
